import React from 'react'
import type { CutoffScenario, CutoffsPayload } from '@/types'

/**
 * Two shapes:
 *
 *   <CutoffMessage variant="error" minDate="2026-06-10" />
 *     Loud red banner for when the customer's selected date is too early.
 *     Universal copy ("Changes can't be made for the selected date").
 *
 *   <CutoffMessage variant="info" scenario="increase" cutoffs={cutoffs} />
 *     Calm info note for the always-visible "here's the rule" hint.
 *     Per-scenario copy, lead day count pulled from /api/auth/cutoffs.
 *
 * Both render their date / lead-day values dynamically from the backend
 * cutoffs payload, so config tweaks on portal-backend flow through with
 * no frontend code change. See docs/FEDEV-456.md.
 */
interface ErrorProps {
  variant: 'error'
  // Kept for backwards-compat with existing callers; not used after the
  // switch to static copy. The dynamic earliest-date pull was unreliable
  // (wrong values surfacing on weekends; UAT issues) so Laura asked for
  // a fixed message that doesn't depend on the cutoffs API state.
  minDate?: string
}

interface InfoProps {
  variant: 'info'
  scenario: CutoffScenario
  cutoffs: CutoffsPayload
}

type CutoffMessageProps = ErrorProps | InfoProps

const styles: Record<string, React.CSSProperties> = {
  error: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderLeft: '4px solid #dc2626',
    borderRadius: '6px',
    padding: '0.875rem 1rem',
    color: '#7f1d1d',
    marginBottom: '1rem',
  },
  errorTitle: {
    display: 'block',
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: '0.35rem',
  },
  errorBody: {
    margin: 0,
    fontSize: '0.95rem',
    lineHeight: 1.45,
  },
  info: {
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderLeft: '4px solid #3b82f6',
    borderRadius: '6px',
    padding: '0.75rem 1rem',
    color: '#1e3a8a',
    margin: '0.5rem 0',
  },
  infoBody: {
    margin: 0,
    fontSize: '0.9rem',
    lineHeight: 1.45,
  },
}

const CutoffMessage: React.FC<CutoffMessageProps> = (props) => {
  if (props.variant === 'error') {
    return (
      <div className="cutoff-error" style={styles.error} role="alert">
        <strong style={styles.errorTitle}>
          Changes can't be made for the selected date
        </strong>
        <p style={styles.errorBody}>
          We need 3 days to give our team time to prepare any additional orders.
        </p>
      </div>
    )
  }

  const { scenario, cutoffs } = props
  const lead = cutoffs.cutoffs[scenario].lead_working_days

  return (
    <div className="cutoff-info" style={styles.info}>
      <p style={styles.infoBody}>{infoCopy(scenario, lead)}</p>
    </div>
  )
}

/**
 * Per-scenario informational copy. Singular vs plural ("1 day's" vs
 * "3 days'") generated from the API lead count so config tweaks
 * don't need code changes here.
 */
function infoCopy(scenario: CutoffScenario, lead: number): string {
  const possessive = lead === 1 ? "day's" : "days'"
  const dayWord = lead === 1 ? 'day' : 'days'

  switch (scenario) {
    case 'increase':
      return `To give our teams time to prepare your order, quantity increases require at least ${lead} ${possessive} notice.`
    case 'add_new_line':
      return `To give our teams time to prepare your order, new items require at least ${lead} ${possessive} notice.`
    case 'decrease':
      return `To give our teams time to prepare your order, quantity decreases require at least ${lead} ${possessive} notice.`
    case 'remove_line':
      return `To give our teams time to prepare your order, removing items requires at least ${lead} ${possessive} notice.`
    case 'cancel_subscription':
      return `To give our teams time to prepare, cancelling a subscription requires at least ${lead} ${possessive} notice.`
    case 'holiday_add':
      return `To give our teams time to prepare, booking a holiday requires at least ${lead} ${possessive} notice.`
    case 'holiday_end_early':
      return `To give our teams time to prepare, ending a holiday early requires at least ${lead} ${possessive} notice.`
    case 'holiday_remove_before_start':
      return `To give our teams time to prepare, cancelling a holiday requires at least ${lead} ${possessive} notice.`
    default:
      // Fallback — shouldn't normally fire because the type is exhaustive,
      // but keeps us safe if a new scenario lands without a matching case.
      return `Changes require at least ${lead} ${dayWord} notice.`
  }
}

export default CutoffMessage
